/**
 * Saison eines permanenten Events (docs/bereiche/permanente-events.md). Ein Stoneman ist nicht
 * das ganze Jahr fahrbar, sondern von Frühjahr bis Herbst: `seasonFrom`/`seasonUntil` sind
 * Monate (1–12), beide leer heißt ganzjährig. Über den Jahreswechsel geht auch (11 bis 3).
 * „In Saison" / „Saisonpause" wird daraus **errechnet**, niemand schaltet es um. Gespiegelt in
 * `PermanentSeason` (Backend, `model/Event.kt`) für Sortierung und Mails.
 */

/** Beschriftung, wo ein permanentes Event sonst sein Datum zeigt. */
export const PERMANENT_LABEL = 'Permanent';

export const SEASON_MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const SEASON_MONTHS_SHORT = ['Jan', 'Feb', 'März', 'Apr', 'Mai', 'Juni', 'Juli', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export interface SeasonFields {
  permanent?: boolean | null;
  seasonFrom?: number | null;
  seasonUntil?: number | null;
}

const isMonth = (m: unknown): m is number => typeof m === 'number' && Number.isInteger(m) && m >= 1 && m <= 12;

/** Saison als Monatspaar, `null` = ganzjährig (auch wenn nur eine Hälfte gesetzt ist). */
export function eventSeason(e: SeasonFields): { from: number; until: number } | null {
  return isMonth(e.seasonFrom) && isMonth(e.seasonUntil) ? { from: e.seasonFrom, until: e.seasonUntil } : null;
}

/** Liegt der Monat in der Saison? Über den Jahreswechsel (11 bis 3) eingeschlossen. */
export function monthInSeason(month: number, from: number, until: number): boolean {
  return from <= until ? month >= from && month <= until : month >= from || month <= until;
}

/** „April bis Oktober", „nur im Juli", „ganzjährig". */
export function seasonLabel(e: SeasonFields): string {
  const s = eventSeason(e);
  if (!s) return 'ganzjährig';
  if (s.from === s.until) return `nur im ${SEASON_MONTHS[s.from - 1]}`;
  return `${SEASON_MONTHS[s.from - 1]} bis ${SEASON_MONTHS[s.until - 1]}`;
}

/** Kurzform für die Card: „Apr–Okt", „Juli", „ganzjährig". */
export function seasonShortLabel(e: SeasonFields): string {
  const s = eventSeason(e);
  if (!s) return 'ganzjährig';
  if (s.from === s.until) return SEASON_MONTHS_SHORT[s.from - 1];
  return `${SEASON_MONTHS_SHORT[s.from - 1]}–${SEASON_MONTHS_SHORT[s.until - 1]}`;
}

/** Was die Card an Stelle des Datums zeigt: „Permanent · Apr–Okt". `null` bei einem Termin. */
export function permanentBadge(e: SeasonFields): string | null {
  if (!e.permanent) return null;
  return `${PERMANENT_LABEL} · ${seasonShortLabel(e)}`;
}

/**
 * „in Saison" / „Saisonpause" — an der Stelle, an der ein Termin seinen Countdown trägt.
 * `null` bei ganzjährigen Events (dort gäbe es nur den einen Zustand) und bei Terminen.
 */
export function seasonState(e: SeasonFields, now: Date = new Date()): 'in Saison' | 'Saisonpause' | null {
  if (!e.permanent) return null;
  const s = eventSeason(e);
  if (!s) return null;
  return monthInSeason(now.getMonth() + 1, s.from, s.until) ? 'in Saison' : 'Saisonpause';
}

/** Permanentes Event außerhalb seiner Saison — die Card steht dann grau (`EventCardModel.offSeason`). */
export function isOffSeason(e: SeasonFields, now: Date = new Date()): boolean {
  return seasonState(e, now) === 'Saisonpause';
}

/**
 * Was Card und Detailseite außerhalb der Saison an Stelle des Countdowns zeigen: „wieder ab April".
 * Der Monat sagt mehr als ein Zustandswort — „Saisonpause" stand dort bis zum 13.10.2026 und
 * hat niemand verstanden. In der Saison und ganzjährig `null`.
 */
export function offSeasonLabel(e: SeasonFields, now: Date = new Date()): string | null {
  if (!isOffSeason(e, now)) return null;
  return `wieder ab ${SEASON_MONTHS[eventSeason(e)!.from - 1]}`;
}

/** Ausgeschrieben für Detailseite und Kopfzeilen: „Saison April bis Oktober", „nur im Juli", „ganzjährig". */
export function seasonLongLabel(e: SeasonFields): string {
  const s = eventSeason(e);
  if (!s) return 'ganzjährig';
  return s.from === s.until ? seasonLabel(e) : `Saison ${seasonLabel(e)}`;
}

/** An der Stelle des Datums auf der Detailseite: „Permanent · Saison April bis Oktober". */
export function permanentDateLabel(e: SeasonFields): string | null {
  if (!e.permanent) return null;
  return `${PERMANENT_LABEL} · ${seasonLongLabel(e)}`;
}

/**
 * Eine Seite der Saison im Formular gewählt (Web `SeasonSelect`, App `EventProposalForm`):
 * wer nur den Anfang wählt, bekommt denselben Monat als Ende vorbelegt, damit nie eine halbe
 * Saison gespeichert wird; „ganzjährig" (`null`) auf einer Seite leert beide.
 */
export function pickSeasonMonth(
  side: 'from' | 'until', month: number | null, from: number | null, until: number | null,
): { seasonFrom: number | null; seasonUntil: number | null } {
  if (month == null) return { seasonFrom: null, seasonUntil: null };
  return side === 'from'
    ? { seasonFrom: month, seasonUntil: until ?? month }
    : { seasonFrom: from ?? month, seasonUntil: month };
}
